import * as React from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';

import { FormField } from 'components/form';
import SearchableDropdown from 'components/inputs/searchable-dropdown/SearchableDropdown';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';

import { searchToDropdownItems } from 'modules/campaigns/reducers/campaignBrief';

import { __t, applyTranslations } from 'utils/language';

import { MixedSelectDropdownItem, OrganizationMetaCommonFields, State } from 'types';

import './campaign-meta-fields.scss';

applyTranslations(require('./campaign-meta-fields.i18n.json'));

type StateProps = {
  isZalandoMerchantsFieldHidden?: boolean
}

type OwnProps = {
  brands?: Array<OrganizationMetaCommonFields>,
  merchants?: Array<OrganizationMetaCommonFields>,
  isBrandModified?: boolean,
  isMerchantModified?: boolean,
  direction?: 'row' | 'column',
  onResetToDefault?: (metaFieldType: 'brands' | 'merchants') => void,
  onChangeMetaFields: (metaFieldType: 'brands' | 'merchants', items: Array<OrganizationMetaCommonFields>) => void
}

type Props = OwnProps & StateProps;

const CampaignMetaFields = ({
  brands,
  merchants,
  direction,
  isBrandModified,
  isMerchantModified,
  isZalandoMerchantsFieldHidden,
  onResetToDefault,
  onChangeMetaFields
}: Props) => {
  const [selectedBrands, setBrands] = React.useState(brands);
  const [selectedMerchants, setMerchants] = React.useState(brands);
  const [hasBrandsModified, setHasBrandsModified] = React.useState(isBrandModified);
  const [hasMerchantsModified, setHasMerchantsModified] = React.useState(isMerchantModified);

  React.useEffect(() => {
    setBrands(brands);
    setMerchants(merchants);
  }, [brands, merchants]);

  const handleMetaFieldsChange = (metaFieldType: 'brands' | 'merchants', items: Array<MixedSelectDropdownItem>) => {
    const modifiedItems: Array<OrganizationMetaCommonFields> = [];

    items.forEach((item) => {
      if (item.identifier.includes('--') && metaFieldType === 'brands' && item.isSelected) {
        (item.data || []).forEach((subBrand: OrganizationMetaCommonFields) => {
          modifiedItems.push({
            name: subBrand.name,
            code: subBrand.code
          });
        });
      } else if (item.isSelected) {
        modifiedItems.push({
          name: item.label,
          [metaFieldType === 'brands' ? 'code' : 'id']: item.identifier
        });
      }
    });

    if (onResetToDefault) {
      if (metaFieldType === 'brands') {
        setHasBrandsModified(true);
      } else {
        setHasMerchantsModified(true);
      }
    }

    onChangeMetaFields(metaFieldType, modifiedItems);
  };

  const handleResetToDefault = (metaFieldType: 'brands' | 'merchants') => {
    if (onResetToDefault) {
      onResetToDefault(metaFieldType);

      if (metaFieldType === 'brands') {
        setHasBrandsModified(false);
      } else {
        setHasMerchantsModified(false);
      }
    }
  };

  const renderComment = (key: 'brands' | 'merchants') => (
    <div className="campaign-meta-fields__form-field-comment">
      {__t(`campaign_meta_fields.field_${key}_comment`)}
      <span className="campaign-meta-fields__form-field-note">
        {__t(`campaign_meta_fields.field_${key}_note`)}
      </span>
    </div>
  );

  const renderDropdown = (key: 'brands' | 'merchants', data?: Array<OrganizationMetaCommonFields>) => (
    <div className="campaign-meta-fields__form-field">
      {
          ((key === 'brands' && hasBrandsModified) || (key === 'merchants' && hasMerchantsModified)) && (
            <div
              className={`campaign-meta-fields__default-metadata campaign-meta-fields__default-metadata--${key}`}
              onClick={() => handleResetToDefault(key)}
            >
              <div className="campaign-meta-fields__default-metadata-icon">
                <SVGDynamicIcon
                  name="rotate-left-custom-fg"
                  width={16}
                  height={14}
                  customColor="grey"
                />
              </div>
              <span
                className="campaign-meta-fields__default-metadata_text"
              >
                {`${__t('campaign_meta_fields.reset_to_default')}`}
              </span>
            </div>
          )
        }
      <FormField
        showValidationIcon={false}
        fontSize="small"
        label={__t(`campaign_meta_fields.field_${key}_label`)}
        comment={renderComment(key)}
        tooltipMessage={(__t(`campaign_meta_fields.field_${key}_tooltip`))}
        tooltipWidth="long"
        labelType="highlighted"
        direction="column"
      >
        <SearchableDropdown
          withSearchable={false}
          items={(data || []).map(item => ({
            identifier: item.code || item.id || '',
            value: item.code || item.id || '',
            label: `${item.companyName ? `${item.name} - ${item.companyName}` : item.name}${item.isFamily ? ' (All)' : ''}`,
            isSelected: true,
            isDisabled: false
          }))}
          placeholder={__t(`campaign_meta_fields.field_${key}_placeholder`)}
          dataFetcher={(query) => searchToDropdownItems(query, key)}
          withCloseOnSelect={false}
          withSavedInput
          shouldUpdateItems
          onItemsUpdate={(items: Array<MixedSelectDropdownItem>) => handleMetaFieldsChange(key, items)}
          withTruncate
        />
      </FormField>
    </div>
  );

  const classes = classnames(
    'campaign-meta-fields__metadata-fields-container',
    [`campaign-meta-fields__metadata-fields-container--direction-${direction || 'column'}`]
  );

  return (
    <div>
      <div className={classes}>
        {renderDropdown('brands', selectedBrands)}
        {!isZalandoMerchantsFieldHidden && renderDropdown('merchants', selectedMerchants)}
      </div>
    </div>
  );
};

const mapStateToProps = ({ runtimeConfig }: State): StateProps => ({
  isZalandoMerchantsFieldHidden: runtimeConfig.config?.isZalandoMerchantsFieldHidden
});

export default connect(mapStateToProps, null)(CampaignMetaFields);
